import { network } from "../config";
import TypeDefaultResponse from "../types/TypeDefaultResponse";
import { TypeMessage, TypeSessionChatResponse } from "../types/TypeMessageChat";
import { TypeUpdatedUserInfo } from "../types/TypeUsers";
import {
  CreateWebinarDataDto,
  TypeWebinarsFilters,
  Webinar,
  WebinarFileTypes,
  WebinarsParamsAvailable,
} from "../types/Webinar";

import url from "../utils/formApiUrl";
import socket from "../utils/socket";
import customKy from "./kyExtend";

const baseURL = process.env.REACT_APP_HTTP_CONNECTION_STRING + "webinars/";

const getAuthHeader = (token: string) => ({
  Authorization: `Bearer ${token}`,
});

const { webinar } = network;

export default class WebinarApi {
  static async create(
    token: string,
    payload: {
      newWebinar: CreateWebinarDataDto;
    },
  ): Promise<TypeDefaultResponse & { webinar?: Webinar }> {
    return await customKy
      .post(url(baseURL, webinar.create), {
        headers: getAuthHeader(token),
        json: payload,
      })
      .then((res) => res.json());
  }

  static async getMy(
    token: string,
  ): Promise<TypeDefaultResponse & { webinars?: Webinar[] }> {
    return await customKy
      .get(url(baseURL, ""), {
        headers: getAuthHeader(token),
      })
      .then((res) => res.json());
  }

  static async getWebinarById(
    token: string,
    id: string,
  ): Promise<Webinar | null> {
    return await customKy
      .get(url(baseURL, webinar.getWebinarById, { id }), {
        headers: getAuthHeader(token),
      })
      .then((res) => res.json());
  }

  static async update(
    token: string,
    payload: TypeUpdatedUserInfo,
  ): Promise<TypeDefaultResponse & { webinar?: Webinar }> {
    const body = {
      updateData: {
        ...payload,
      },
    };

    return await customKy
      .patch(url(baseURL, webinar.update, { id: payload._id }), {
        headers: getAuthHeader(token),
        json: body,
      })
      .then((res) => res.json());
  }

  static async getSpecialistWebinars(
    token: string,
    payload: {
      specialistUserId: string;
      specialistId?: string;
      page: number;
      limit: number;
    },
  ): Promise<TypeDefaultResponse & { webinars?: Webinar[] }> {
    const params = new URLSearchParams();
    if (payload.specialistId) {
      params.append("specialistId", payload.specialistId);
    }

    return await customKy
      .get(
        url(baseURL, webinar.getSpecialistWebinars, {
          id: payload.specialistUserId,
          page: payload.page,
          limit: payload.limit,
        }),
        {
          headers: getAuthHeader(token),
        },
      )
      .then((res) => res.json());
  }

  static async getAllWebinarFileSignedUrls(
    token: string,
    id: string,
  ): Promise<
    TypeDefaultResponse & {
      urls?: {
        avatar?: string;
        video?: string;
        pdfPresentation?: string;
        waitingScreenDesktopImage?: string;
        waitingScreenMobileImage?: string;
        certificate?: string;
      };
    }
  > {
    return await customKy
      .get(url(baseURL, webinar.getAllWebinarFileSignedUrls, { id }), {
        headers: getAuthHeader(token),
      })
      .then((res) => res.json());
  }

  static async deleteWebinarFile(
    token: string,
    payload: {
      webinarId: string;
      type: WebinarFileTypes;
    },
  ): Promise<TypeDefaultResponse & { isApproved?: boolean }> {
    return await customKy
      .delete(
        url(baseURL, webinar.deleteWebinarFile, { id: payload.webinarId }),
        {
          headers: getAuthHeader(token),
          json: payload,
        },
      )
      .then((res) => res.json());
  }

  static async delete(
    token: string,
    webinarId: string,
  ): Promise<TypeDefaultResponse> {
    return await customKy
      .delete(url(baseURL, webinar.delete, { id: webinarId }), {
        headers: getAuthHeader(token),
      })
      .then((res) => res.json());
  }

  static async getFilteredWebinars(
    token: string,
    filters: TypeWebinarsFilters,
  ): Promise<
    TypeDefaultResponse & {
      webinars?: Webinar[];
      paramsAvailable: WebinarsParamsAvailable;
    }
  > {
    return await customKy
      .post(
        url(baseURL, webinar.getFilteredWebinars, {
          page: filters.page,
          limit: filters.limit,
        }),
        {
          headers: getAuthHeader(token),
          json: { filters },
          timeout: 30000,
        },
      )
      .then((res) => res.json());
  }

  static async getWebinarAuthorsRecommendations(payload: {
    webinarId: string;
    page: number;
    limit: number;
  }): Promise<
    TypeDefaultResponse & {
      webinars?: Webinar[];
      totalAmount?: number;
    }
  > {
    return await customKy
      .get(
        url(baseURL, webinar.getWebinarAuthorsRecommendations, {
          page: payload.page,
          limit: payload.limit,
          id: payload.webinarId,
        }),
        {
          timeout: 30000,
        },
      )
      .then((res) => res.json());
  }

  static async purchaseWebinar(
    token: string,
    webinarId: string,
  ): Promise<
    TypeDefaultResponse & {
      webinar?: Webinar;
    }
  > {
    return await customKy
      .post(
        url(baseURL, webinar.purchaseWebinar, {
          id: webinarId,
        }),
        {
          headers: getAuthHeader(token),
          timeout: 30000,
        },
      )
      .then((res) => res.json());
  }

  static async getWebinarChat(
    token: string,
    id: string,
  ): Promise<
    TypeDefaultResponse & {
      chat?: TypeSessionChatResponse;
      avatars?: { id: string; avatar: string; specialistId?: string }[];
    }
  > {
    return await customKy
      .get(url(baseURL, webinar.getWebinarChat, { id }), {
        headers: getAuthHeader(token),
      })
      .then((res) => res.json());
  }

  static async sendMessageToWebinarChat(payload: {
    webinarId: string;
    message: string;
    date: Date;
    userId: string;
  }): Promise<
    TypeDefaultResponse & {
      newMessage?: TypeMessage | null;
    }
  > {
    return new Promise((resolve) => {
      socket.emit(webinar.sendMessage, payload);
      socket.once(webinar.onMessage, (response) => {
        resolve(response);
      });
    });
  }

  /* Files actions */
  static async generateS3uploadUrlForFile(
    token: string,
    payload: {
      webinarId: string;
      filename: string;
      filetype: string;
      type: WebinarFileTypes;
    },
  ): Promise<
    TypeDefaultResponse & {
      url?: string;
      filepath?: string;
      type?: WebinarFileTypes;
    }
  > {
    return await customKy

      .post(
        url(baseURL, webinar.generateS3uploadUrlForFile, {
          id: payload.webinarId,
        }),
        {
          headers: getAuthHeader(token),
          json: payload,
        },
      )
      .then((res) => res.json());
  }

  static async addFileToWebinar(
    token: string,
    payload: { webinarId: string; filepath: string; type: WebinarFileTypes },
  ): Promise<
    TypeDefaultResponse & {
      url?: string;
      filepath?: string;
    }
  > {
    return await customKy
      .put(
        url(baseURL, webinar.addFileToWebinar, {
          id: payload.webinarId,
        }),
        {
          headers: getAuthHeader(token),
          json: payload,
        },
      )
      .then((res) => res.json());
  }

  static async initiateFileMultipartUpload(
    token: string,
    payload: {
      webinarId: string;
      filename: string;
      filetype: string;
      type: WebinarFileTypes;
    },
  ): Promise<
    TypeDefaultResponse & {
      uploadId?: string;
      filepath?: string;
      type?: WebinarFileTypes;
    }
  > {
    return await customKy

      .post(
        url(baseURL, webinar.initiateFileMultipartUpload, {
          id: payload.webinarId,
        }),
        {
          headers: getAuthHeader(token),
          json: payload,
        },
      )
      .then((res) => res.json());
  }

  static async generateFilePartUrl(
    token: string,
    payload: {
      filepath: string;
      uploadId: string;
      partNumber: number;
    },
  ): Promise<
    TypeDefaultResponse & {
      url?: string;
      partNumber?: number;
    }
  > {
    return await customKy

      .post(url(baseURL, webinar.generateFilePartUrl), {
        headers: getAuthHeader(token),
        json: payload,
      })
      .then((res) => res.json());
  }

  static async completeFileMultipartUpload(
    token: string,
    payload: {
      webinarId: string;
      filepath: string;
      uploadId: string;
      parts: { ETag: string; PartNumber: number }[];
      type: WebinarFileTypes;
    },
  ): Promise<TypeDefaultResponse> {
    return await customKy
      .put(
        url(baseURL, webinar.completeFileMultipartUpload, {
          id: payload.webinarId,
        }),
        {
          headers: getAuthHeader(token),
          json: payload,
        },
      )
      .then((res) => res.json());
  }
  /* End files actions */
}
